import {makeAutoObservable, observable, runInAction, reaction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import type {IRanking} from "data/types/rankings";
import {Bindings} from "data/constants/bindings";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";
import type {IUserRosterStore} from "data/stores/user_roster/user_roster.store";
import type {IContestPlayerStatsStore} from "data/stores/contest_player_stats/contest_player_stats.store";
import {RequestState, ModalType} from "data/enums";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {extractErrorMessage, wait} from "data/utils";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {compact} from "lodash";
import type {IGamePlayer, IPlayerCalculations} from "data/utils/player.calculations";

interface IParams {
	rowRanking: IRanking;
	contestId: number;
}

export interface ILeaderboardRowController extends ViewController<IParams> {
	i18n: ILocalizationStore;
	get isOwn(): boolean;
	get isOpenRoster(): boolean;
	get hasRoster(): boolean;
	get players(): IGamePlayer[];
	get isLoading(): boolean;
	showRoster: () => void;
	closeRoster: () => void;
}

@injectable()
export class LeaderboardRowController implements ILeaderboardRowController {
	@observable _rowRanking: IRanking | null = null;
	@observable _contestId: number = 0;
	@observable _disposers: ReturnType<typeof reaction>[] = [];
	@observable _isOpenRoster: boolean = false;
	@observable _requestState: RequestState = RequestState.IDLE;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.RankingsStore) private readonly _rankingsStore: IRankingsStore,
		@inject(Bindings.UserRosterStore) private readonly _userRosterStore: IUserRosterStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore,
		@inject(Bindings.PlayerCalculations)
		private readonly _playerCalculations: IPlayerCalculations,
		@inject(Bindings.ContestPlayerStatsStore)
		private readonly _contestPlayerStatsStore: IContestPlayerStatsStore
	) {
		makeAutoObservable(this);
	}

	get userRanking(): IRanking | null {
		return this._rankingsStore.userRankings;
	}

	get isOwn() {
		return this.userId === this.userRanking?.userId;
	}

	get isOpenRoster() {
		return this._isOpenRoster;
	}

	get hasRoster(): boolean {
		return this._userRosterStore.userId === this.userId;
	}

	get players(): IGamePlayer[] {
		const players = this._userRosterStore.userRoster?.players ?? [];
		return compact(
			players.map((player) => {
				return this._playerCalculations.getGamePlayerById(player.playerId, this._contestId);
			})
		);
	}

	get userId() {
		return this._rowRanking?.userId;
	}

	get isLoading(): boolean {
		return [RequestState.IDLE, RequestState.PENDING].includes(this._requestState);
	}

	init(param: IParams) {
		this._contestId = param.contestId;
		this._rowRanking = param.rowRanking;
		this._disposers = [
			reaction(
				() => this.hasRoster,
				(isActiveRoster) => {
					this._isOpenRoster = isActiveRoster;
				}
			),
		];
	}

	dispose() {
		this._disposers.forEach((dispose) => dispose());
	}

	onChange(param: IParams) {
		this._contestId = param.contestId;
		this._rowRanking = param.rowRanking;
	}

	closeRoster = async () => {
		this._isOpenRoster = false;
		await wait(500);
		this._userRosterStore.clear();
	};

	showRoster = () => {
		const userId = this.userId;
		if (this._isOpenRoster) {
			void this.closeRoster();
			return;
		}
		this._isOpenRoster = true;
		this._requestState = RequestState.PENDING;
		if (userId) {
			void Promise.all([this._userRosterStore.fetchUserRoster(this._contestId, this.userId)])
				.then(() => {
					runInAction(() => {
						this._requestState = RequestState.SUCCESS;
					});
				})
				.catch((e) => {
					runInAction(() => {
						this._requestState = RequestState.ERROR;
					});

					this._modalsStore.showModal(ModalType.ERROR, {
						message: extractErrorMessage(e),
					});
				});
		}
	};
}
